import { toast } from 'sonner';
import type { CurrentUser } from './types';
import axios from 'axios';

export interface LoginFormData {
  email: string;
  password: string;
}

export class AuthStore {
  #user: null | CurrentUser = null;
  #loading: boolean = false;
  #error: null | unknown = null;
  #isAuthenticated: boolean = false;
  #userDoc: null | unknown = null;

  getUser() {
    return this.#user;
  }

  getLoading() {
    return this.#loading;
  }

  getError() {
    return this.#error;
  }

  getIsAuthenticated() {
    return this.#isAuthenticated;
  }

  getUserDoc() {
    return this.#userDoc;
  }

  async fetchUser() {
    try {
      const res = await axios.get(
        `${import.meta.env.VITE_API_BASE_URL}/v1/me`,
        {
          withCredentials: true,
        },
      );
      this.#user = res.data.role ? res.data : null;
      this.#loading = false;
      this.#isAuthenticated = res.data.role === 'unauth' ? false : true;
      this.#error = null;
    } catch (err) {
      console.error('fetchUser err', err);
      this.#user = null;
      this.#loading = false;
      this.#isAuthenticated = false;
      this.#error = err;
    }
  }

  async login(formData: LoginFormData) {
    try {
      const res = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/auth/login`,
        formData,
        { withCredentials: true },
      );
      this.#user = res.data.user;
      this.#loading = false;
      this.#isAuthenticated = true;
      this.#error = null;
    } catch (err) {
      console.error(err);
      this.#user = null;
      this.#loading = false;
      this.#isAuthenticated = false;
      this.#error = err;
    }
  }

  async logout() {
    try {
      await axios.get(`${import.meta.env.VITE_API_BASE_URL}/auth/logout`, {
        withCredentials: true,
      });
      this.#user = null;
      this.#loading = false;
      this.#isAuthenticated = false;
      this.#error = null;
    } catch (err) {
      console.error(err);
      this.#user = null;
      this.#loading = false;
      this.#isAuthenticated = false;
      this.#error = err;
    }
  }

  async fetchUserDoc() {
    if (!this.#user?.userId) {
      await this.fetchUser();
    }

    try {
      const res = await axios.get(
        `${import.meta.env.VITE_API_BASE_URL}/v1/users/${this.getUser()?.userId}`,
        { withCredentials: true },
      );
      this.#userDoc = res.data;
      this.#loading = false;
      this.#error = null;
    } catch (err) {
      console.error(err);
      this.#userDoc = null;
      this.#loading = false;
      this.#error = err;
    }
  }

  // @ts-expect-error - type formData later
  async putUserDoc(formData) {
    if (!this.#user?.userId) {
      await this.fetchUser();
    }

    try {
      const res = await axios.put(
        `${import.meta.env.VITE_API_BASE_URL}/v1/users/${this.getUser()?.userId}`,
        formData,
        { withCredentials: true },
      );

      // in case the user changed their name
      await this.fetchUser();

      this.#userDoc = res.data;
      this.#loading = false;
      this.#error = null;

      toast.success('Update succeeded.', { duration: 10000 });
    } catch (err) {
      console.error(err);
      this.#error = err;
      this.#loading = false;
      this.#userDoc = null;
    }
  }
}
